import React, { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { Popover } from 'antd';
import { IconButton, IconButton_IconSizes, Title } from '@ydistri/ds';
import { MdFilterAlt } from 'react-icons/md';
import PopupFiltersWrapper from './PopupFiltersWrapper';

const defaultIcon = <MdFilterAlt size={IconButton_IconSizes.small} />;

interface EntityListsFiltersActionProps extends PropsWithChildren {
  title?: string;
  tooltip?: string;
  filterSet?: boolean;
  icon?: React.ReactNode;
  resetFilters?: () => void;
}

/**
 * An action button that shows a popover meant to display filters.
 * The body of the popover is provided as children by the user of this component.
 * @param title - Title of the popover
 * @param tooltip - Tooltip of the action button
 * @param filterSet - Whether some filters are applied or not. If true, the button is highlighted.
 * @param icon - Icon to be used for the action button
 * @param resetFilters - Optional reset filters function. If provided, a "Reset filters" button is shown.
 * @param children - Content of the popover
 */
const PopupFiltersAction: React.FC<EntityListsFiltersActionProps> = ({
  title = 'Filters',
  tooltip = 'Show filters',
  filterSet = false,
  icon = defaultIcon,
  resetFilters,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const closePopover = useCallback(() => {
    setOpen(false);
  }, []);

  const filtersContent = useMemo(
    () => (
      <PopupFiltersWrapper
        closePopover={closePopover}
        resetFilters={resetFilters}
        filterSet={filterSet}
      >
        {children}
      </PopupFiltersWrapper>
    ),
    [children, closePopover, filterSet, resetFilters],
  );

  return (
    <Popover
      trigger="click"
      content={filtersContent}
      placement="bottom"
      title={<Title $size="small">{title}</Title>}
      open={open}
      onOpenChange={setOpen}
    >
      <IconButton
        size="small"
        tooltip={tooltip}
        icon={icon}
        colorScheme={filterSet ? theme.iconColorSchemes.default : undefined}
      />
    </Popover>
  );
};

export default PopupFiltersAction;
